<template>
    <div class="row">
        <div class="col-12">
            <div class="card">
                <div class="card-body">
                    <div class="card-header-2 d-flex align-center justify-space-between">
                        <h5>CJ Products Explorer</h5>
                        <v-btn color="primary" :loading="cjPreview.loading" @click="getPreviewProducts">Search Products</v-btn>
                    </div>

                    <v-alert
                        v-if="message"
                        :type="messageType"
                        dismissible
                        class="mb-4"
                    >
                        {{ message }}
                    </v-alert>

                    <div class="row">
                        <div class="col-sm-4">
                            <v-text-field v-model="cjPreview.filters.pid" label="PID"></v-text-field>
                        </div>
                        <div class="col-sm-4">
                            <v-text-field v-model="cjPreview.filters.productNameEn" label="Product name (EN)"></v-text-field>
                        </div>
                        <div class="col-sm-4">
                            <v-text-field v-model="cjPreview.filters.productSku" label="Product SKU"></v-text-field>
                        </div>
                        <div class="col-sm-4">
                            <v-select
                                v-model="cjPreview.filters.localCategoryId"
                                :items="cjPreview.localCategoryOptions"
                                item-title="title"
                                item-text="text"
                                item-value="value"
                                label="Category"
                                clearable
                                :loading="cjPreview.loadingLocalCategories"
                                no-data-text="No local categories found"
                            ></v-select>
                        </div>
                        <div class="col-sm-2">
                            <v-text-field v-model="cjPreview.filters.countryCode" label="Country" placeholder="US"></v-text-field>
                        </div>
                        <div class="col-sm-2">
                            <v-text-field v-model="cjPreview.filters.startInventory" label="Min stock" type="number"></v-text-field>
                        </div>
                        <div class="col-sm-2">
                            <v-text-field v-model="cjPreview.filters.endInventory" label="Max stock" type="number"></v-text-field>
                        </div>
                        <div class="col-sm-2">
                            <v-text-field v-model="cjPreview.filters.startPrice" label="Min price" type="number"></v-text-field>
                        </div>
                        <div class="col-sm-2">
                            <v-text-field v-model="cjPreview.filters.endPrice" label="Max price" type="number"></v-text-field>
                        </div>
                        <div class="col-sm-2">
                            <v-select
                                v-model="cjPreview.filters.pageSize"
                                :items="[6, 12, 24, 48]"
                                label="Page size"
                                @update:modelValue="onPageSizeChanged"
                            ></v-select>
                        </div>
                    </div>

                    <v-alert
                        v-if="!cjPreview.loading && filteredRows.length === 0"
                        type="info"
                        variant="tonal"
                        class="mt-2"
                    >
                        No products match the current filters.
                    </v-alert>

                    <div class="mt-4">
                        <div v-for="product in filteredRows" :key="product.pid || product.productSku" class="mb-4">
                            <v-card variant="outlined" class="pa-3" @mouseenter="ensurePreviewDetails(product)">
                                <div class="row">
                                    <div class="col-sm-3">
                                        <v-img
                                            :src="resolvePreviewImage(product)"
                                            cover
                                            height="180"
                                            class="rounded"
                                        ></v-img>
                                    </div>
                                    <div class="col-sm-9">
                                        <div class="d-flex justify-space-between flex-wrap gap-2">
                                            <div>
                                                <h6 class="mb-1">{{ product.productNameEn || product.productName || 'Unnamed product' }}</h6>
                                                <div><strong>PID:</strong> {{ product.pid || '-' }}</div>
                                                <div><strong>SKU:</strong> {{ product.productSku || '-' }}</div>
                                            </div>
                                            <div class="d-flex gap-2 align-start flex-wrap">
                                                <v-chip size="small" color="primary" variant="tonal">Price ref: {{ extractPreviewPrice(product) }}</v-chip>
                                                <v-chip size="small" color="secondary" variant="tonal">Type: {{ product.productType || '-' }}</v-chip>
                                            </div>
                                        </div>

                                        <div class="row mt-2">
                                            <div class="col-sm-4">
                                                <div><strong>Category:</strong> {{ resolveCategoryName(product) }}</div>
                                                <div><strong>Price band:</strong> {{ resolvePriceBand(product) }}</div>
                                                <div><strong>Weight:</strong> {{ resolveWeightInfo(product) }}</div>
                                                <div><strong>Dimensions:</strong> {{ resolveDimensionsInfo(product) }}</div>
                                            </div>
                                            <div class="col-sm-4">
                                                <div><strong>Supplier:</strong> {{ resolveSupplierName(product) }}</div>
                                                <div><strong>Supplier ID:</strong> {{ resolveSupplierId(product) }}</div>
                                                <div><strong>CJ reviews:</strong> {{ resolveCommentSummary(product) }}</div>
                                                <div><strong>Avg rating:</strong> {{ resolveAverageCommentScore(product) }}</div>
                                                <div><strong>Sales signal:</strong> {{ resolveSalesTrustInfo(product) }}</div>
                                                <div><strong>Ships to:</strong> {{ resolveShippingCoverage(product) }}</div>
                                                <div><strong>Supplier catalog:</strong> {{ resolveSupplierCatalogInfo(product) }}</div>
                                                <div><strong>Product status:</strong> {{ resolveProductStatus(product) }}</div>
                                            </div>
                                            <div class="col-sm-4">
                                                <div><strong>Variants:</strong> {{ resolveVariantCount(product) }}</div>
                                                <div><strong>Total inventory:</strong> {{ resolveInventoryInfo(product) }}</div>
                                                <div><strong>Main warehouses:</strong> {{ resolveMainWarehouses(product) }}</div>
                                                <div><strong>Sample variant:</strong> {{ resolveVariantSample(product) }}</div>
                                            </div>
                                        </div>

                                        <div class="d-flex gap-2 mt-3 flex-wrap align-center">
                                            <v-btn size="small" color="info" variant="outlined" :loading="Boolean(cjPreview.loadingDetailByPid[product.pid])" @click="ensurePreviewDetails(product, { complete: true })">
                                                Load detail
                                            </v-btn>
                                            <v-btn size="small" color="primary" variant="outlined" :loading="Boolean(cjPreview.loadingDetailByPid[product.pid])" @click="copyProductPayload(product)">
                                                Copy CJ JSON
                                            </v-btn>
                                            <v-btn size="small" color="success" :loading="Boolean(cjPreview.importingByPid[product.pid])" @click="addSinglePreviewProduct(product)">
                                                Add base product
                                            </v-btn>
                                            <v-select
                                                v-model="cjPreview.importMode"
                                                :items="[{ title: 'Single variant', value: 'single' }, { title: 'All variants', value: 'all' }]"
                                                item-title="title"
                                                item-value="value"
                                                density="compact"
                                                hide-details
                                                style="max-width: 180px; min-width: 160px;"
                                                label="Import mode"
                                            ></v-select>
                                            <v-btn size="small" color="warning" :loading="Boolean(cjPreview.importingVariantsByPid[product.pid])" @click="addAllPreviewVariants(product)">
                                                Import variants
                                            </v-btn>
                                        </div>
                                    </div>
                                </div>
                            </v-card>
                        </div>
                    </div>

                    <div v-if="cjPreview.rows.length > 0" class="d-flex flex-wrap align-center justify-space-between gap-3 mt-4">
                        <div>
                            <strong>Page:</strong> {{ cjPreview.pagination.pageNum }} / {{ totalPages }}
                            <span class="ms-2"><strong>Total products:</strong> {{ cjPreview.pagination.total }}</span>
                        </div>
                        <div class="d-flex align-center gap-2">
                            <v-btn
                                size="small"
                                variant="outlined"
                                :disabled="cjPreview.loading || cjPreview.pagination.pageNum <= 1"
                                @click="changePage(cjPreview.pagination.pageNum - 1)"
                            >
                                Previous
                            </v-btn>
                            <v-pagination
                                :model-value="cjPreview.pagination.pageNum"
                                :length="totalPages"
                                :total-visible="7"
                                density="comfortable"
                                @update:modelValue="changePage"
                            ></v-pagination>
                            <v-btn
                                size="small"
                                variant="outlined"
                                :disabled="cjPreview.loading || cjPreview.pagination.pageNum >= totalPages"
                                @click="changePage(cjPreview.pagination.pageNum + 1)"
                            >
                                Next
                            </v-btn>
                        </div>
                    </div>

                    <v-card v-if="apiResponse" class="mt-4 pa-4">
                        <pre>{{ JSON.stringify(apiResponse, null, 2) }}</pre>
                    </v-card>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    name: 'cj-products-explorer',
    title: 'CJ Products Explorer',
    data() {
        return {
            message: '',
            messageType: 'info',
            apiResponse: null,
            baseUrl: process.env.VUE_APP_URL_API_BASE + '/admin/linkapis',
            cjPreview: {
                loading: false,
                rows: [],
                loadingDetailByPid: {},
                detailsByPid: {},
                completeDetailByPid: {},
                importingByPid: {},
                importingVariantsByPid: {},
                loadingLocalCategories: false,
                localCategoryOptions: [],
                cjCategoryNameIndex: {},
                lastCategoryBeforeIdentityFilter: null,
                pagination: {
                    pageNum: 1,
                    pageSize: 12,
                    total: 0,
                },
                importMode: 'single',
                filters: {
                    pid: '',
                    productNameEn: '',
                    productSku: '',
                    countryCode: 'US',
                    localCategoryId: null,
                    startInventory: '',
                    endInventory: '',
                    startPrice: '',
                    endPrice: '',
                    pageNum: 1,
                    pageSize: 12,
                },
            },
        }
    },
    watch: {
        'cjPreview.filters.productSku'() {
            this.syncCategorySelectionWithIdentityFilter()
        },
        'cjPreview.filters.pid'() {
            this.syncCategorySelectionWithIdentityFilter()
        },
    },
    computed: {
        totalPages() {
            const pageSize = Number(this.cjPreview.pagination.pageSize || this.cjPreview.filters.pageSize || 12)
            const total = Number(this.cjPreview.pagination.total || 0)
            if (pageSize <= 0) {
                return 1
            }
            return Math.max(1, Math.ceil(total / pageSize))
        },

        selectedLocalCategoryLabel() {
            if (!this.cjPreview.filters.localCategoryId) {
                return ''
            }
            const selected = this.cjPreview.localCategoryOptions.find(
                (item) => String(item.value) === String(this.cjPreview.filters.localCategoryId),
            )
            return selected?.title || selected?.text || ''
        },

        filteredRows() {
            const categoryLabel = this.normalizeLabel(this.selectedLocalCategoryLabel)
            const hasIdentityFilter = this.isIdentityFilterActive()
            const minPrice = this.toNumberOrNull(this.cjPreview.filters.startPrice)
            const maxPrice = this.toNumberOrNull(this.cjPreview.filters.endPrice)

            return this.cjPreview.rows.filter((product) => {
                if (!hasIdentityFilter && categoryLabel) {
                    const cjCategory = this.getMultilangText(product?.categoryName || product?.categoryNameEn || '')
                    if (!this.matchesCategory(cjCategory, categoryLabel)) {
                        return false
                    }
                }

                if (minPrice === null && maxPrice === null) {
                    return true
                }

                const comparablePrice = this.resolveComparableProductPrice(product)
                if (comparablePrice === null) {
                    return false
                }

                if (minPrice !== null && comparablePrice < minPrice) {
                    return false
                }

                if (maxPrice !== null && comparablePrice > maxPrice) {
                    return false
                }

                return true
            })
        },
    },
    async mounted() {
        await Promise.all([this.loadLocalCategories(), this.loadCjCategoryIndex()])
        this.syncCategorySelectionWithIdentityFilter()
        await this.getPreviewProducts()
    },
    methods: {
        getMultilangText(textObj) {
            if (!textObj) {
                return ''
            }
            if (typeof textObj === 'string') {
                return textObj.trim()
            }
            if (typeof textObj === 'object') {
                return String(textObj.es || textObj.en || textObj.fr || '').trim()
            }
            return ''
        },

        async loadLocalCategories() {
            this.cjPreview.loadingLocalCategories = true
            try {
                const response = await axios.get(`${process.env.VUE_APP_URL_API_BASE}/category/list`, {
                    responseType: 'json',
                })

                const rows = Array.isArray(response.data) ? response.data : []
                const options = rows
                    .map((item) => ({
                        value: item?.id,
                        title: this.getMultilangText(item?.name),
                        text: this.getMultilangText(item?.name),
                            isParent: item?.categoryFather === null,
                    }))
                    .filter((item) => item.value !== null && item.value !== undefined && item.title !== '')

                options.sort((a, b) => a.title.localeCompare(b.title))
                this.cjPreview.localCategoryOptions = options
                this.applyDefaultLocalCategory()
            } catch (error) {
                this.cjPreview.localCategoryOptions = []
                this.message = `Error loading local categories: ${error?.response?.data?.message || error.message}`
                this.messageType = 'error'
            } finally {
                this.cjPreview.loadingLocalCategories = false
            }
        },

        isIdentityFilterActive() {
            return String(this.cjPreview.filters.productSku || '').trim() !== '' || String(this.cjPreview.filters.pid || '').trim() !== ''
        },

        getDefaultLocalCategoryOption() {
            const options = this.cjPreview.localCategoryOptions
            if (!options.length) {
                return null
            }
            const preferredName = this.normalizeLabel('Pet Bows & Ties')
            const preferredCategory = options.find((opt) => this.normalizeLabel(opt.title) === preferredName)
            const firstMapped = options.find((opt) => this.resolveCjCategoryIdByLocalName(opt.title))
            const firstParent = options.find((opt) => opt.isParent)
            return preferredCategory || firstMapped || firstParent || options[0]
        },

        applyDefaultLocalCategory() {
            if (this.cjPreview.filters.localCategoryId) {
                return
            }
            const option = this.getDefaultLocalCategoryOption()
            if (option) {
                this.cjPreview.filters.localCategoryId = option.value
            }
        },

        syncCategorySelectionWithIdentityFilter() {
            const hasIdentityFilter = this.isIdentityFilterActive()

            if (hasIdentityFilter) {
                if (this.cjPreview.filters.localCategoryId) {
                    this.cjPreview.lastCategoryBeforeIdentityFilter = this.cjPreview.filters.localCategoryId
                    this.cjPreview.filters.localCategoryId = null
                }
                return
            }

            if (!this.cjPreview.filters.localCategoryId) {
                const lastSelected = this.cjPreview.lastCategoryBeforeIdentityFilter
                const restored = this.cjPreview.localCategoryOptions.find(
                    (item) => String(item.value) === String(lastSelected),
                )
                if (restored) {
                    this.cjPreview.filters.localCategoryId = restored.value
                    return
                }
                this.applyDefaultLocalCategory()
            }
        },

        normalizeLabel(text) {
            return String(text || '')
                .normalize('NFD')
                .replace(/[\u0300-\u036f]/g, '')
                    .replace(/&/g, ' and ')
                    .replace(/[^a-zA-Z0-9\s]/g, ' ')
                .toLowerCase()
                    .replace(/\s+/g, ' ')
                .trim()
        },

            flattenCjCategoryNames(categories) {
                const map = {}
                const add = (name, id) => {
                    const key = this.normalizeLabel(name)
                    if (!key || !id) {
                        return
                    }
                    if (!map[key]) {
                        map[key] = String(id)
                    }
                }

                categories.forEach((first) => {
                    add(first?.categoryFirstName || first?.categoryName, first?.categoryFirstId || first?.categoryId)

                    const secondList = Array.isArray(first?.categoryFirstList) ? first.categoryFirstList : []
                    secondList.forEach((second) => {
                        add(second?.categorySecondName || second?.categoryName, second?.categorySecondId || second?.categoryId)

                        const thirdList = Array.isArray(second?.categorySecondList) ? second.categorySecondList : []
                        thirdList.forEach((third) => {
                            add(third?.categoryThirdName || third?.categoryName, third?.categoryThirdId || third?.categoryId)
                        })
                    })
                })

                return map
            },

            async loadCjCategoryIndex() {
                try {
                    const response = await axios.get(`${this.baseUrl}/categories`, {
                        responseType: 'json',
                    })
                    const root = response?.data?.data || {}
                    const categories = Array.isArray(root?.data) ? root.data : []
                    this.cjPreview.cjCategoryNameIndex = this.flattenCjCategoryNames(categories)
                } catch (_) {
                    this.cjPreview.cjCategoryNameIndex = {}
                }
            },

            resolveCjCategoryIdByLocalName(localCategoryName) {
                const normalized = this.normalizeLabel(localCategoryName)
                if (!normalized) {
                    return null
                }
                return this.cjPreview.cjCategoryNameIndex[normalized] || null
            },

            matchesCategory(cjCategoryRaw, selectedCategoryNormalized) {
                const cjCategory = this.normalizeLabel(cjCategoryRaw)
                const selectedCategory = this.normalizeLabel(selectedCategoryNormalized)

                if (!selectedCategory) {
                    return true
                }
                if (!cjCategory) {
                    return false
                }

                if (cjCategory.includes(selectedCategory) || selectedCategory.includes(cjCategory)) {
                    return true
                }

                const cjTokens = new Set(cjCategory.split(' ').filter((token) => token.length > 2))
                const selectedTokens = selectedCategory.split(' ').filter((token) => token.length > 2)
                if (selectedTokens.length === 0) {
                    return false
                }

                let common = 0
                selectedTokens.forEach((token) => {
                    if (cjTokens.has(token)) {
                        common += 1
                    }
                })

                return common >= 2 || (common / selectedTokens.length) >= 0.6
            },
        buildPreviewParams() {
            const f = this.cjPreview.filters
            const params = {
                pageNum: Number(f.pageNum || 1),
                pageSize: Number(f.pageSize || 12),
            }

            const localCategoryName = this.selectedLocalCategoryLabel
            const cjCategoryId = this.resolveCjCategoryIdByLocalName(localCategoryName)
            const hasIdentityFilter = this.isIdentityFilterActive()
            if (cjCategoryId && !hasIdentityFilter) {
                params.categoryId = cjCategoryId
            }

            const optional = ['pid', 'productNameEn', 'productSku', 'countryCode', 'startInventory', 'endInventory', 'startPrice', 'endPrice']
            optional.forEach((key) => {
                const value = f[key]
                if (value !== null && value !== undefined && String(value).trim() !== '') {
                    params[key] = String(value).trim()
                }
            })

            return params
        },

        extractProductsPayload(responseData) {
            const root = responseData?.data || {}
            const nested = root?.data || root
            const products = Array.isArray(nested?.products) ? nested.products : []
            return products
        },

        extractPaginationPayload(responseData) {
            const root = responseData?.data || {}
            const nested = root?.data || root
            const pagination = nested?.pagination || {}
            return {
                pageNum: Number(pagination?.pageNum || this.cjPreview.filters.pageNum || 1),
                pageSize: Number(pagination?.pageSize || this.cjPreview.filters.pageSize || 12),
                total: Number(pagination?.total || 0),
            }
        },

        async getPreviewProducts() {
            this.syncCategorySelectionWithIdentityFilter()

            this.cjPreview.loading = true
            try {
                const response = await axios.get(`${this.baseUrl}/products`, {
                    params: this.buildPreviewParams(),
                    responseType: 'json',
                })

                this.cjPreview.rows = this.extractProductsPayload(response.data)
                this.cjPreview.pagination = this.extractPaginationPayload(response.data)
                this.cjPreview.filters.pageNum = this.cjPreview.pagination.pageNum
                this.cjPreview.filters.pageSize = this.cjPreview.pagination.pageSize
                this.message = `Products loaded: ${this.cjPreview.rows.length}, filtered: ${this.filteredRows.length} (page ${this.cjPreview.pagination.pageNum}/${this.totalPages})`
                this.messageType = 'success'
            } catch (error) {
                this.cjPreview.rows = []
                this.cjPreview.pagination.total = 0
                this.message = `Error loading products: ${error?.response?.data?.message || error.message}`
                this.messageType = 'error'
                this.apiResponse = error?.response?.data || null
            } finally {
                this.cjPreview.loading = false
            }
        },

        async changePage(page) {
            const nextPage = Number(page)
            if (Number.isNaN(nextPage) || nextPage < 1 || nextPage === this.cjPreview.filters.pageNum) {
                return
            }
            this.cjPreview.filters.pageNum = nextPage
            await this.getPreviewProducts()
        },

        async onPageSizeChanged() {
            this.cjPreview.filters.pageNum = 1
            await this.getPreviewProducts()
        },

        extractProductDetailData(responseData) {
            const root = responseData?.data || {}
            return root?.data || root || null
        },

        async ensurePreviewDetails(product, options = {}) {
            const pid = product?.pid
            const complete = Boolean(options?.complete)
            if (!pid) {
                return null
            }
            if (complete && this.cjPreview.completeDetailByPid[pid] && this.cjPreview.detailsByPid[pid]) {
                return this.cjPreview.detailsByPid[pid]
            }
            if (!complete && this.cjPreview.detailsByPid[pid]) {
                return this.cjPreview.detailsByPid[pid]
            }

            this.cjPreview.loadingDetailByPid = {
                ...this.cjPreview.loadingDetailByPid,
                [pid]: true,
            }

            try {
                const endpoint = complete
                    ? `${this.baseUrl}/product-complete/${encodeURIComponent(pid)}/pid/false`
                    : `${this.baseUrl}/product-inventory/${encodeURIComponent(pid)}/pid`
                const response = await axios.get(endpoint, {
                    responseType: 'json',
                })
                const detail = this.extractProductDetailData(response.data)
                this.cjPreview.detailsByPid = {
                    ...this.cjPreview.detailsByPid,
                    [pid]: detail,
                }
                if (complete) {
                    this.cjPreview.completeDetailByPid = {
                        ...this.cjPreview.completeDetailByPid,
                        [pid]: true,
                    }
                }
                return detail
            } catch (error) {
                this.message = `Error loading detail for ${pid}: ${error?.response?.data?.message || error.message}`
                this.messageType = 'error'
                return null
            } finally {
                this.cjPreview.loadingDetailByPid = {
                    ...this.cjPreview.loadingDetailByPid,
                    [pid]: false,
                }
            }
        },

        buildSupplierClipboardPayload(product, detail) {
            return {
                supplierId: this.getFirstDefinedValue([detail, product], ['supplierId']),
                supplierName: this.getFirstDefinedValue([detail, product], ['supplierName']),
                supplier: detail?.supplier ?? product?.supplier ?? null,
                comments: detail?.comments || null,
                metrics: detail?.metrics || null,
            }
        },

        async copyTextToClipboard(text) {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text)
                return
            }

            const textarea = document.createElement('textarea')
            textarea.value = text
            textarea.setAttribute('readonly', '')
            textarea.style.position = 'absolute'
            textarea.style.left = '-9999px'
            document.body.appendChild(textarea)
            textarea.select()
            document.execCommand('copy')
            document.body.removeChild(textarea)
        },

        async copyProductPayload(product) {
            const pid = product?.pid
            if (!pid) {
                this.message = 'PID no disponible para copiar JSON.'
                this.messageType = 'error'
                return
            }

            try {
                const detail = await this.ensurePreviewDetails(product, { complete: true })
                const payload = {
                    copiedAt: new Date().toISOString(),
                    pid,
                    sku: product?.productSku || null,
                    product,
                    detail,
                    supplier: this.buildSupplierClipboardPayload(product, detail),
                }

                await this.copyTextToClipboard(JSON.stringify(payload, null, 2))
                this.message = `JSON de CJ copiado al portapapeles para ${pid}.`
                this.messageType = 'success'
            } catch (error) {
                this.message = `Error copiando JSON de ${pid}: ${error?.message || error}`
                this.messageType = 'error'
            }
        },

        resolvePreviewImage(product) {
            if (product?.bigImage) {
                return product.bigImage
            }

            if (product?.productImage) {
                try {
                    const images = JSON.parse(product.productImage)
                    if (Array.isArray(images) && images.length > 0) {
                        return images[0]
                    }
                } catch (_) {
                    return product.productImage
                }
            }

            return ''
        },

        extractPreviewPrice(product) {
            const keys = ['sellPrice', 'variantSellPrice', 'freeShippingPrice', 'suggestSellPrice', 'sourcePrice', 'price']
            for (const key of keys) {
                const value = product?.[key]
                if (value !== null && value !== undefined && value !== '' && !Number.isNaN(Number(value))) {
                    return `${key}: ${this.formatPriceWithUnit(value)}`
                }
            }
            return 'N/A'
        },

        resolveVariantCount(product) {
            const pid = product?.pid
            const detail = pid ? this.cjPreview.detailsByPid[pid] : null
            if (Array.isArray(detail?.variants)) {
                return detail.variants.length
            }
            if (Array.isArray(product?.variants)) {
                return product.variants.length
            }
            return '-'
        },

        getDetailByProduct(product) {
            const pid = product?.pid
            return pid ? this.cjPreview.detailsByPid[pid] : null
        },

        getFirstDefinedValue(sources, keys) {
            for (const source of sources) {
                if (!source || typeof source !== 'object') {
                    continue
                }
                for (const key of keys) {
                    const value = source?.[key]
                    if (value !== null && value !== undefined && String(value).trim() !== '') {
                        return value
                    }
                }
            }
            return null
        },

        toNumberOrNull(value) {
            if (value === null || value === undefined) {
                return null
            }

            if (typeof value === 'string' && value.trim() === '') {
                return null
            }

            const num = Number(value)
            return Number.isNaN(num) ? null : num
        },

        formatPriceWithUnit(value) {
            const amount = this.toNumberOrNull(value)
            if (amount === null) {
                return 'N/A'
            }
            return `${amount.toFixed(2)} USD`
        },

        formatWeightWithUnit(value) {
            const amount = this.toNumberOrNull(value)
            if (amount === null) {
                return '-'
            }
            return `${amount} g`
        },

        resolveComparableProductPrice(product) {
            const detail = this.getDetailByProduct(product)
            const sources = [product, detail]
            const keys = ['sellPrice', 'variantSellPrice', 'freeShippingPrice', 'suggestSellPrice', 'sourcePrice', 'price']

            for (const source of sources) {
                if (!source || typeof source !== 'object') {
                    continue
                }

                for (const key of keys) {
                    const value = this.toNumberOrNull(source?.[key])
                    if (value !== null) {
                        return value
                    }
                }
            }

            return null
        },

        getCommentList(product) {
            const detail = this.getDetailByProduct(product)
            return Array.isArray(detail?.comments?.list) ? detail.comments.list : []
        },

        extractCommentScore(comment) {
            return this.toNumberOrNull(
                comment?.score ?? comment?.productScore ?? comment?.star ?? comment?.stars ?? comment?.rating,
            )
        },

        resolveCommentSummary(product) {
            const detail = this.getDetailByProduct(product)
            const total = this.toNumberOrNull(detail?.comments?.total ?? detail?.metrics?.total_comments)
            const retrieved = this.toNumberOrNull(detail?.comments?.retrieved ?? detail?.metrics?.retrieved_comments)
            if (total === null && retrieved === null) {
                return this.cjPreview.completeDetailByPid[product?.pid] ? 'No reviews in CJ' : 'Load detail'
            }
            if (retrieved !== null && total !== null && retrieved !== total) {
                return `${total} total (${retrieved} fetched)`
            }
            return `${total ?? retrieved} reviews`
        },

        resolveAverageCommentScore(product) {
            const scores = this.getCommentList(product)
                .map((comment) => this.extractCommentScore(comment))
                .filter((score) => score !== null)
            if (scores.length === 0) {
                return this.cjPreview.completeDetailByPid[product?.pid] ? '-' : 'Load detail'
            }
            const average = scores.reduce((sum, score) => sum + score, 0) / scores.length
            return `${average.toFixed(1)}/5`
        },

        resolveSalesTrustInfo(product) {
            const detail = this.getDetailByProduct(product)
            const sales = this.toNumberOrNull(this.getFirstDefinedValue([detail, product], ['sellNum', 'orderNum', 'listedNum']))
            if (sales === null) {
                return '-'
            }
            if (sales >= 1000) {
                return `${sales} sold (high)`
            }
            if (sales >= 100) {
                return `${sales} sold (medium)`
            }
            return `${sales} sold (low)`
        },

        extractShippingCountries(product) {
            const detail = this.getDetailByProduct(product)
            const direct = this.getFirstDefinedValue([detail, product], ['shippingCountryCodes'])
            if (Array.isArray(direct)) {
                return direct.filter((item) => String(item || '').trim() !== '')
            }

            const variants = Array.isArray(detail?.variants) ? detail.variants : []
            const countries = new Set()
            variants.forEach((variant) => {
                const inventory = Array.isArray(variant?.inventory) ? variant.inventory : []
                inventory.forEach((row) => {
                    const code = row?.countryCode || row?.warehouseCountryCode || row?.country
                    if (String(code || '').trim() !== '') {
                        countries.add(String(code).trim())
                    }
                })
            })
            return Array.from(countries)
        },

        resolveShippingCoverage(product) {
            const countries = this.extractShippingCountries(product)
            if (countries.length === 0) {
                return this.cjPreview.completeDetailByPid[product?.pid] ? '-' : 'Load detail'
            }
            const preview = countries.slice(0, 4).join(', ')
            const suffix = countries.length > 4 ? ` +${countries.length - 4}` : ''
            return `${countries.length} countries: ${preview}${suffix}`
        },

        resolveSupplierCatalogInfo(product) {
            const detail = this.getDetailByProduct(product)
            const count = this.getFirstDefinedValue(
                [detail, product, detail?.supplier, product?.supplier],
                ['supplierProductCount', 'supplierProductNum', 'productCount', 'supplierGoodsNum', 'goodsNum'],
            )
            if (count === null) {
                return this.cjPreview.completeDetailByPid[product?.pid] ? 'Not provided by CJ payload' : 'Load detail'
            }
            return `${count} products`
        },

        resolveCategoryName(product) {
            const detail = this.getDetailByProduct(product)
            return this.getFirstDefinedValue([detail, product], ['categoryName', 'categoryNameEn', 'category']) || '-'
        },

        resolvePriceBand(product) {
            const detail = this.getDetailByProduct(product)
            const variants = Array.isArray(detail?.variants) ? detail.variants : []
            const prices = []

            const directCandidates = ['sellPrice', 'variantSellPrice', 'freeShippingPrice', 'suggestSellPrice', 'sourcePrice', 'price']
            directCandidates.forEach((key) => {
                const value = this.toNumberOrNull(product?.[key])
                if (value !== null) {
                    prices.push(value)
                }
            })

            variants.forEach((variant) => {
                directCandidates.forEach((key) => {
                    const value = this.toNumberOrNull(variant?.[key])
                    if (value !== null) {
                        prices.push(value)
                    }
                })
            })

            if (prices.length === 0) {
                return 'N/A'
            }

            const min = Math.min(...prices)
            const max = Math.max(...prices)
            if (min === max) {
                return this.formatPriceWithUnit(min)
            }
            return `${this.formatPriceWithUnit(min)} - ${this.formatPriceWithUnit(max)}`
        },

        resolveWeightInfo(product) {
            const detail = this.getDetailByProduct(product)
            const value = this.getFirstDefinedValue([detail, product], ['packWeight', 'weight', 'productWeight'])
            return this.formatWeightWithUnit(value)
        },

        resolveDimensionsInfo(product) {
            const detail = this.getDetailByProduct(product)
            const source = detail || product || {}
            const length = this.getFirstDefinedValue([source], ['packLength', 'productLength', 'length'])
            const width = this.getFirstDefinedValue([source], ['packWidth', 'productWidth', 'width'])
            const height = this.getFirstDefinedValue([source], ['packHeight', 'productHeight', 'height'])
            if (length === null && width === null && height === null) {
                return '-'
            }
            return `${length || '?'} x ${width || '?'} x ${height || '?'}`
        },

        resolveSupplierId(product) {
            const detail = this.getDetailByProduct(product)
            return this.getFirstDefinedValue([detail, product], ['supplierId']) || '-'
        },

        resolveListingMetric(product) {
            const detail = this.getDetailByProduct(product)
            const listed = this.getFirstDefinedValue([detail, product], ['listedNum', 'sellNum', 'orderNum'])
            return listed === null ? '-' : `${listed}`
        },

        resolveProductStatus(product) {
            const detail = this.getDetailByProduct(product)
            return this.getFirstDefinedValue([detail, product], ['status', 'productStatus', 'listingStatus']) || '-'
        },

        resolveMainWarehouses(product) {
            const detail = this.getDetailByProduct(product)
            const variants = Array.isArray(detail?.variants) ? detail.variants : []
            if (variants.length === 0) {
                return '-'
            }

            const warehouseTotals = new Map()
            variants.forEach((variant) => {
                const inventory = Array.isArray(variant?.inventory) ? variant.inventory : []
                inventory.forEach((item) => {
                    const name = this.getFirstDefinedValue([item], ['warehouseName', 'warehouse', 'areaName', 'countryCode']) || 'unknown'
                    const amount = this.toNumberOrNull(item?.storageNum) || 0
                    warehouseTotals.set(name, (warehouseTotals.get(name) || 0) + amount)
                })
            })

            const top = Array.from(warehouseTotals.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, 3)
                .map(([name, qty]) => `${name} (${qty})`)

            return top.length ? top.join(', ') : '-'
        },

        resolveVariantSample(product) {
            const detail = this.getDetailByProduct(product)
            const variants = Array.isArray(detail?.variants) ? detail.variants : []
            if (variants.length === 0) {
                return '-'
            }
            const first = variants[0]
            const sku = this.getFirstDefinedValue([first], ['variantSku']) || '-'
            const vid = this.getFirstDefinedValue([first], ['vid']) || '-'
            return `${sku} / ${vid}`
        },

        resolveSupplierName(product) {
            const pid = product?.pid
            const detail = pid ? this.cjPreview.detailsByPid[pid] : null
            if (detail?.supplierName) {
                return detail.supplierName
            }
            if (detail?.supplierId) {
                return `supplierId: ${detail.supplierId}`
            }
            if (product?.supplierName) {
                return product.supplierName
            }
            return '-'
        },

        resolveInventoryInfo(product) {
            const pid = product?.pid
            const detail = pid ? this.cjPreview.detailsByPid[pid] : null
            const variants = Array.isArray(detail?.variants) ? detail.variants : []
            if (variants.length === 0) {
                return '-'
            }

            let total = 0
            variants.forEach((variant) => {
                const inventory = Array.isArray(variant?.inventory) ? variant.inventory : []
                inventory.forEach((item) => {
                    const storageNum = Number(item?.storageNum || 0)
                    total += Number.isNaN(storageNum) ? 0 : storageNum
                })
            })

            return total
        },

        async importProductIntoDatabase(identifier, type, importMode = 'single') {
            const response = await axios.get(`${this.baseUrl}/product-complete/${encodeURIComponent(identifier)}/${type}/true/${encodeURIComponent(importMode)}`, {
                responseType: 'json',
            })
            return response.data
        },

        async addSinglePreviewProduct(product) {
            const pid = product?.pid
            if (!pid) {
                this.message = 'PID no disponible para importacion.'
                this.messageType = 'error'
                return
            }

            this.cjPreview.importingByPid = {
                ...this.cjPreview.importingByPid,
                [pid]: true,
            }

            try {
                const importMode = this.cjPreview.importMode || 'single'
                const payload = await this.importProductIntoDatabase(pid, 'pid', importMode)
                this.apiResponse = payload
                this.message = payload?.message || 'Producto importado correctamente.'
                this.messageType = payload?.error ? 'error' : 'success'
            } catch (error) {
                this.message = `Error importando producto ${pid}: ${error?.response?.data?.message || error.message}`
                this.messageType = 'error'
            } finally {
                this.cjPreview.importingByPid = {
                    ...this.cjPreview.importingByPid,
                    [pid]: false,
                }
            }
        },

        async addAllPreviewVariants(product) {
            const pid = product?.pid
            if (!pid) {
                this.message = 'PID no disponible para importar variantes.'
                this.messageType = 'error'
                return
            }

            this.cjPreview.importingVariantsByPid = {
                ...this.cjPreview.importingVariantsByPid,
                [pid]: true,
            }

            try {
                const importMode = this.cjPreview.importMode || 'single'
                const detail = await this.ensurePreviewDetails(product)
                const variants = Array.isArray(detail?.variants) ? detail.variants : []

                if (importMode === 'all') {
                    const result = await this.importProductIntoDatabase(pid, 'pid', 'all')
                    this.apiResponse = result
                    this.message = result?.message || `Importacion de variantes finalizada.`
                    this.messageType = result?.error ? 'error' : 'success'
                    return
                }

                const variantSkus = variants
                    .map((variant) => (typeof variant?.variantSku === 'string' ? variant.variantSku.trim() : ''))
                    .filter((sku) => sku !== '')

                if (variantSkus.length === 0) {
                    this.message = `No hay variantes disponibles para ${pid}.`
                    this.messageType = 'error'
                    return
                }

                const firstVariantSku = variantSkus[0]
                const result = await this.importProductIntoDatabase(firstVariantSku, 'variantSku', 'single')
                this.apiResponse = result
                this.message = result?.message || `Variante importada: ${firstVariantSku}`
                this.messageType = result?.error ? 'error' : 'success'
            } catch (error) {
                this.message = `Error importando variantes de ${pid}: ${error?.response?.data?.message || error.message}`
                this.messageType = 'error'
            } finally {
                this.cjPreview.importingVariantsByPid = {
                    ...this.cjPreview.importingVariantsByPid,
                    [pid]: false,
                }
            }
        },
    },
}
</script>

<style scoped>
pre {
    white-space: pre-wrap;
    word-break: break-word;
}
</style>
